/**
 * Real self-fetching Expenses queue hook (#22792) — replaces the store-backed
 * `MOCK_EXPENSES` fixture. Mirrors `use_employees.ts`/`use_pay_runs.ts`'s
 * established pattern: the real bearer client (`getTrpcClient()`,
 * `lib/trpc.ts`) + the shared `useOAuthEntityContext()` helper, DI-testable
 * fetch/mutation functions, an explicit state machine
 * (`loading`/`no_entity`/`entitlement_required`/`error`/`ready`), no mock
 * fallback anywhere.
 *
 * ── Three real reads, one shared entity context ──────────────────────────
 *
 *  - `expenseClaim.list` — the claims themselves (`payroll:employer:read`/
 *    `:write`, #22791/#22799). Single page, `limit: 200`, no `status` filter
 *    (`draft`/`voided` rows are dropped client-side by
 *    `isQueueableClaimStatus`, not server-side) — this app has no pagination
 *    UI anywhere yet (mirrors every other list read here). The 200-row cap
 *    is on the ENTITY'S TOTAL claim count, not on queueable ones only: a
 *    heavy drafter can silently push older, real submitted/approved/paid/
 *    rejected claims out of this page before they're ever dropped for being
 *    `draft`/`voided` (adversarial review, #22792) — an entity with more
 *    than 200 lifetime claims of ANY status is out of scope for this pass.
 *  - `payroll.listEmployees` — resolves `submitter_user_id` -> employee name
 *    (same scope pair, already used by `use_employees.ts`).
 *  - `payroll.listPayRuns` — resolves a payroll-method claim's
 *    `payroll_run_id` -> settlement period (same scope pair). Deliberately
 *    narrow: only the summary rows' `id`/`period_start` are read, never
 *    `getPayRun`/`getPayRunJournalPreview` (unlike `use_pay_runs.ts`'s own
 *    full `fetchPayRunRecords`, which this hook does NOT reuse) — this
 *    screen never needs a run's lines or journal preview.
 *
 * `deriveExpensesFromRecords` (`wagepro_store_expenses.ts`) is the pure
 * adapter all three feed into.
 *
 * ── entitlement_required vs error ─────────────────────────────────────────
 *
 * `expenseClaim.list`/`.approve`/`.reject` are gated by
 * `assertFeatureEnabled(..., 'expense_claims')` — a Starter+ PLAN-TIER gate,
 * NOT the `payroll` add-on entitlement `isPayrollAddonRequiredError`
 * classifies. Classified here via `isPlanGateError`/`isSubscriptionGateError`
 * (`lib/trpc.ts`) instead, which read the errorFormatter's structured
 * `requiredTier`/`subscriptionRequired` wire fields directly rather than
 * matching a message string. Every OTHER FORBIDDEN (missing OAuth scope, a
 * null-audience token, no entity access) resolves the generic `error` state
 * — same "don't mask a real auth bug behind a misleading upgrade prompt"
 * stance `use_pay_runs.ts`'s `isPayrollAddonRequiredError` docblock
 * establishes.
 *
 * ── Approve/Reject write path ─────────────────────────────────────────────
 *
 * `approve`/`reject` are `undefined` (hiding the affordance entirely, never
 * a button that would 403) unless ALL of:
 *   - the read resolved `ready`
 *   - `isWriteCapableGrant(grant)` (`use_pay_runs.ts`) — a read-only OAuth
 *     token never sees the buttons
 *   - the caller's `expenses_role` (via `entityUser.me`, already allowlisted
 *     for `payroll:employer:*`) ranks `approver` or higher, OR they are an
 *     adviser — mirrors `requireExpensesRole`'s exact ranking
 *     (`apps/api/src/trpc/permissions.ts`), so the button-hiding rule and the
 *     backend's own consumption-time gate agree.
 *
 * A bare FORBIDDEN reaching the mutation anyway (a stale grant, a race
 * between this hook's own `entityUser.me` read and the actual entity
 * membership) resolves the `forbidden` `ExpenseActionFailure` — distinct
 * from `entitlement_required` (the plan gate) and `fx_rate_required`
 * (`isFxRateRequiredError`, #21805) so `expenses_screen.tsx` can show the
 * right one-line explanation instead of a raw error toast.
 *
 * A failed `entityUser.me` read never poisons the rest of the screen (the
 * claims themselves may still be perfectly readable) — it degrades to
 * `canApprove: false`, i.e. exactly what a read-only grant already gets.
 */
import * as React from 'react';
import {
  getTrpcClient,
  isForbiddenTrpcError,
  isFxRateRequiredError,
  isPlanGateError,
  isSubscriptionGateError,
} from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';
import {
  deriveExpensesFromRecords,
  type ExpensePayRunLookup,
  type ExpenseSubmitterLookup,
  type RealExpenseClaimRecord,
} from './wagepro_store_expenses.js';
// `count_by_status` is owned by the screen's own utils module (mirrors
// `use_dashboard.ts`'s existing cross-import of the same function) rather
// than being re-derived here.
import { count_by_status } from '../screens/expenses/expense_utils.js';
import type { Expense, ExpenseStatus } from './types';

/** Input every `expenseClaim.*`/`payroll.*` call in this module needs to identify the entity. */
export interface ExpensesFetchInput {
  firm_id: string;
  entity_id: string;
}

/** Backend `expense_claims.expenses_role` ranking (mirrors `EXPENSES_ROLE_ORDER`, `apps/api/src/trpc/permissions.ts`). */
type ExpensesRole = 'none' | 'submitter' | 'approver' | 'admin';
const EXPENSES_ROLE_ORDER: readonly ExpensesRole[] = [
  'none',
  'submitter',
  'approver',
  'admin',
];

/**
 * Whether the caller may approve/reject, per `entityUser.me`'s response —
 * mirrors `requireExpensesRole(perms, 'approver')`'s exact ranking
 * (`apps/api/src/trpc/permissions.ts`): an adviser bypasses the check
 * entirely; otherwise `expenses_role` must rank `approver` or higher. Pure
 * and exported so it's unit-testable without any network mocking.
 */
export function hasExpensesApproverAccess(input: {
  expenses_role: ExpensesRole;
  is_adviser: boolean;
}): boolean {
  if (input.is_adviser) return true;
  return (
    EXPENSES_ROLE_ORDER.indexOf(input.expenses_role) >=
    EXPENSES_ROLE_ORDER.indexOf('approver')
  );
}

/** The `entityUser.me` fields this hook reads — a subset of the real response, structurally satisfied by it. */
export interface ExpensesMeLookup {
  perms: { expenses_role: ExpensesRole };
  is_adviser: boolean;
}

const MAX_CLAIMS_PAGE_SIZE = 200;

/** The real `expenseClaim.list`/`payroll.listEmployees`/`payroll.listPayRuns`/`entityUser.me` calls {@link fetchExpensesBundle} needs, isolated behind an injectable seam. */
export interface ExpensesFetchDeps {
  listClaims: (
    input: ExpensesFetchInput & { limit: number },
  ) => Promise<{ claims: RealExpenseClaimRecord[] }>;
  listEmployees: (
    input: ExpensesFetchInput,
  ) => Promise<{ employees: ExpenseSubmitterLookup[] }>;
  listPayRuns: (
    input: ExpensesFetchInput,
  ) => Promise<{ pay_runs: ExpensePayRunLookup[] }>;
  getMe: (input: ExpensesFetchInput) => Promise<ExpensesMeLookup>;
}

function defaultExpensesFetchDeps(): ExpensesFetchDeps {
  const client = getTrpcClient();
  return {
    listClaims: (input) => client.expenseClaim.list.query(input),
    listEmployees: (input) => client.payroll.listEmployees.query(input),
    listPayRuns: (input) => client.payroll.listPayRuns.query(input),
    getMe: (input) => client.entityUser.me.query(input),
  };
}

/** The two ways the real `expenseClaim.list`/`listEmployees`/`listPayRuns` bundle can fail. */
export type ExpensesLoadFailure = 'entitlement_required' | 'error';

function classifyLoadFailure(err: unknown): ExpensesLoadFailure {
  return isPlanGateError(err) || isSubscriptionGateError(err)
    ? 'entitlement_required'
    : 'error';
}

export type ExpensesFetchResult =
  | {
      outcome: 'success';
      expenses: Expense[];
      /** `false` on any `entityUser.me` failure — degrades to the same posture as a read-only grant, never poisons the rest of the read. See module docblock. */
      canApprove: boolean;
    }
  | { outcome: 'failure'; failure: ExpensesLoadFailure };

/**
 * Resolves the entity's real expense-claim queue, or classifies why it
 * couldn't. Exported (and DI'd via `deps`) so the classification is
 * unit-testable against a fake client, mirroring `use_pay_runs.ts`'s
 * `fetchPayRunRecords`.
 */
export async function fetchExpensesBundle(
  input: ExpensesFetchInput,
  deps?: ExpensesFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<ExpensesFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultExpensesFetchDeps();
    const [{ claims }, { employees }, { pay_runs: payRuns }] =
      await Promise.all([
        resolvedDeps.listClaims({ ...input, limit: MAX_CLAIMS_PAGE_SIZE }),
        resolvedDeps.listEmployees(input),
        resolvedDeps.listPayRuns(input),
      ]);
    const expenses = deriveExpensesFromRecords(claims, employees, payRuns);

    // A failed `entityUser.me` read is deliberately NOT fatal to the whole
    // bundle -- see module docblock. Own try/catch, separate from the outer
    // one.
    let canApprove = false;
    try {
      const me = await resolvedDeps.getMe(input);
      canApprove = hasExpensesApproverAccess({
        expenses_role: me.perms.expenses_role,
        is_adviser: me.is_adviser,
      });
    } catch (meErr) {
      console.error(
        '[wagepro-web] expenses: failed to resolve entityUser.me -- approve/reject affordances stay hidden:',
        meErr,
      );
    }

    return { outcome: 'success', expenses, canApprove };
  } catch (err) {
    if (!isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the real expense-claim queue:',
        err,
      );
    }
    return { outcome: 'failure', failure: classifyLoadFailure(err) };
  }
}

export type ExpensesState =
  | { status: 'loading' }
  /** The OAuth grant has no bound entity — structurally unreachable in practice (`main.tsx`'s `AuthGate` gates the whole app on this first), handled defensively anyway. */
  | { status: 'no_entity' }
  /** The entity is missing the Starter+ `expense_claims` plan feature. NEVER render `MOCK_EXPENSES` here. */
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; expenses: Expense[]; canApprove: boolean };

/**
 * Pure derivation, exported separately so it is unit-testable without
 * rendering React — mirrors `resolveEmployeesState`'s precedence exactly.
 */
export function resolveExpensesState(input: {
  entityContext: OAuthEntityContext;
  result: { expenses: Expense[]; canApprove: boolean } | undefined;
  failure: ExpensesLoadFailure | undefined;
}): ExpensesState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.result !== undefined) {
    return {
      status: 'ready',
      expenses: input.result.expenses,
      canApprove: input.result.canApprove,
    };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

// ── Write path: approve / reject ────────────────────────────────────────────

/** How an `expenseClaim.approve`/`.reject` mutation can fail, classified for a human-readable state rather than a raw error toast. */
export type ExpenseActionFailure =
  | 'entitlement_required'
  | 'forbidden'
  | 'fx_rate_required'
  | 'error';

function classifyActionFailure(err: unknown): ExpenseActionFailure {
  if (isPlanGateError(err) || isSubscriptionGateError(err)) {
    return 'entitlement_required';
  }
  if (isFxRateRequiredError(err)) return 'fx_rate_required';
  if (isForbiddenTrpcError(err)) return 'forbidden';
  return 'error';
}

export type ExpenseActionResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: ExpenseActionFailure };

/** The two real mutations {@link approveExpenseClaim}/{@link rejectExpenseClaim} need, isolated behind an injectable seam. */
export interface ExpenseActionDeps {
  approve: (
    input: ExpensesFetchInput & { claim_id: string },
  ) => Promise<unknown>;
  reject: (
    input: ExpensesFetchInput & { claim_id: string; comment: string },
  ) => Promise<unknown>;
}

function defaultExpenseActionDeps(): ExpenseActionDeps {
  const client = getTrpcClient();
  return {
    approve: (input) => client.expenseClaim.approve.mutate(input),
    reject: (input) => client.expenseClaim.reject.mutate(input),
  };
}

/**
 * Calls the real `expenseClaim.approve`. Never throws — every failure mode
 * resolves `{outcome: 'failure', failure}` with one actionable
 * `console.error`, mirroring `use_pay_runs.ts`'s `approvePayRun` never-throw
 * contract (including resolving `deps` INSIDE the try, so a synchronous
 * `getTrpcClient()` throw can't escape as an unhandled rejection).
 */
export async function approveExpenseClaim(
  claim_id: string,
  entityInput: ExpensesFetchInput,
  deps?: ExpenseActionDeps,
): Promise<ExpenseActionResult> {
  try {
    const resolvedDeps = deps ?? defaultExpenseActionDeps();
    await resolvedDeps.approve({ ...entityInput, claim_id });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] expenses: approve failed:', err);
    return { outcome: 'failure', failure: classifyActionFailure(err) };
  }
}

/** Calls the real `expenseClaim.reject`. Same never-throw contract as {@link approveExpenseClaim}. `comment` is REQUIRED by the backend (`z.string().min(1).max(1000)`) — the screen collects it before calling this. */
export async function rejectExpenseClaim(
  claim_id: string,
  comment: string,
  entityInput: ExpensesFetchInput,
  deps?: ExpenseActionDeps,
): Promise<ExpenseActionResult> {
  try {
    const resolvedDeps = deps ?? defaultExpenseActionDeps();
    await resolvedDeps.reject({ ...entityInput, claim_id, comment });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] expenses: reject failed:', err);
    return { outcome: 'failure', failure: classifyActionFailure(err) };
  }
}

export interface UseExpensesResult {
  state: ExpensesState;
  /** Convenience projection: `[]` for every non-`ready` state. */
  expenses: Expense[];
  counts: Record<ExpenseStatus, number>;
  is_loading: boolean;
  /** Re-runs the fetch from scratch — wired to the `error`/`entitlement_required` states' "try again" action. */
  retry: () => void;
  /** `undefined` when the write path isn't reachable -- see module docblock's "Approve/Reject write path" section. */
  approve: ((claim_id: string) => Promise<ExpenseActionResult>) | undefined;
  /** `undefined` when the write path isn't reachable. `comment` is required by the backend. */
  reject:
    | ((claim_id: string, comment: string) => Promise<ExpenseActionResult>)
    | undefined;
}

const EMPTY_COUNTS: Record<ExpenseStatus, number> = {
  pending: 0,
  approved: 0,
  paid: 0,
  rejected: 0,
};

/** Expense claims plus the approve/reject actions the queue screen needs — the real, self-fetching replacement for the old store-backed mock. */
export function useExpenses(): UseExpensesResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  const [result, setResult] = React.useState<
    { expenses: Expense[]; canApprove: boolean } | undefined
  >(undefined);
  const [failure, setFailure] = React.useState<ExpensesLoadFailure | undefined>(
    undefined,
  );
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setResult(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const fetched = await fetchExpensesBundle(
        { firm_id: firmId, entity_id: entityId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (fetched.outcome === 'success') {
        setResult({
          expenses: fetched.expenses,
          canApprove: fetched.canApprove,
        });
      } else {
        setFailure(fetched.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = resolveExpensesState({ entityContext, result, failure });

  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const readyEntity =
    state.status === 'ready' && entityContext.status === 'ready'
      ? entityContext
      : null;
  const canWrite =
    readyEntity !== null &&
    state.status === 'ready' &&
    state.canApprove &&
    isWriteCapableGrant(grant);

  const approve = canWrite
    ? (claim_id: string): Promise<ExpenseActionResult> =>
        approveExpenseClaim(claim_id, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        }).then((actionResult) => {
          if (actionResult.outcome === 'success') retry();
          return actionResult;
        })
    : undefined;

  const reject = canWrite
    ? (claim_id: string, comment: string): Promise<ExpenseActionResult> =>
        rejectExpenseClaim(claim_id, comment, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        }).then((actionResult) => {
          if (actionResult.outcome === 'success') retry();
          return actionResult;
        })
    : undefined;

  return {
    state,
    expenses: state.status === 'ready' ? state.expenses : [],
    counts:
      state.status === 'ready' ? count_by_status(state.expenses) : EMPTY_COUNTS,
    is_loading: state.status === 'loading',
    retry,
    approve,
    reject,
  };
}
